import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import { DEFAULT_WATERMARK_PROPS as DEFAULT_PROPS } from "./watermark.defs";
import type {
  SbWatermarkProps as Props,
  WatermarkBadge,
  WatermarkLogoAlign,
  WatermarkPart,
  WatermarkVariant,
} from "./watermark.types";

/**
 * Branded badge that displays a label alongside the Saasbase logo.
 *
 * @part watermark - rtg-badge: root
 * @part watermark-logo - svg: saasbase logo
 */
@customElement("sb-watermark")
export class SbWatermark extends LitElement implements Props {
  static readonly ROOT = "watermark";
  static readonly LOGO = "logo";

  static readonly TAG = `${NAMESPACE}-${SbWatermark.ROOT}`;

  static readonly PARTS: Record<WatermarkPart, string> = {
    root: SbWatermark.ROOT,
    logo: `${SbWatermark.ROOT}-${SbWatermark.LOGO}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Visual variant of the watermark component.
   * Determines logo color variations and badge font styles.
   */
  @property({ type: String })
  variant: WatermarkVariant = DEFAULT_PROPS.variant;

  /**
   * Visual variant of the badge component.
   */
  @property({ type: String })
  badge: WatermarkBadge = DEFAULT_PROPS.badge;

  /**
   * Alignment of the logo within the badge component.
   * When `"start"`/`"end"`, the logo is rendered before/after the label.
   */
  @property({ type: String, attribute: "logo-align" })
  logoAlign: WatermarkLogoAlign = DEFAULT_PROPS.logoAlign;

  /**
   * Text used as the label of the badge.
   */
  private _label = DEFAULT_PROPS.label;

  @property({ type: String })
  get label() {
    return this._label;
  }

  set label(value: string) {
    const old = this._label;
    this._label = value.length ? value : DEFAULT_PROPS.label;
    this.requestUpdate("label", old);
  }

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbWatermark.TAG}-${this.seed}`;
  }

  get logoId() {
    return `${this.rootId}-${SbWatermark.LOGO}`;
  }

  /* ------------------------------------------------------------ */

  private _renderLogo() {
    return html`
      <svg
        id="${this.logoId}"
        part="${SbWatermark.PARTS.logo}"
        class="saasbase-logo rtg-icon-inline-${this.logoAlign}"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1400 1400"
        fill="none"
      >
        <path
          class="saasbase-logo-cube"
          d="M590.428 29.5039C658.541 -9.83465 742.459 -9.83465 810.572 29.5039L1225.93 269.394C1294.04 308.732 1336 381.433 1336 460.11V939.89C1336 1018.57 1294.04 1091.27 1225.93 1130.61L810.572 1370.5C742.459 1409.83 658.541 1409.83 590.428 1370.5L175.072 1130.61C106.959 1091.27 65 1018.57 65 939.89V460.11C65 381.433 106.959 308.732 175.072 269.394L590.428 29.5039Z"
          fill="#6366F1"
        />
        <path
          class="saasbase-logo-vector"
          d="M699.69 220L320 439.215C297.723 452.077 284 475.846 284 501.569L284 640M284 640L284 898.431C284 924.154 297.723 947.923 320 960.785L543.81 1090M284 640L531.81 783.072C539.236 787.359 543.81 795.282 543.81 803.856V1090M543.81 1090L663.69 1159.21C685.967 1172.08 713.413 1172.08 735.691 1159.21L1079.38 960.785C1095.38 951.547 1095.38 928.453 1079.38 919.215L771.69 741.569C739.69 723.094 739.69 676.906 771.69 658.431L1115.38 460M1115.38 460V700M1115.38 460L907.54 340"
          stroke-width="140"
          stroke-linecap="round"
          stroke="#FFFFFF"
        />
      </svg>
    `;
  }

  /* ---------------------------------------- */

  render() {
    return html`
      <rtg-badge
        id="${this.rootId}"
        part="${SbWatermark.PARTS.root}"
        data-variant="${this.variant}"
        data-badge="${this.badge}"
        variant="${this.badge}"
      >
        ${this.logoAlign === "start"
          ? html`${this._renderLogo()} ${this.label}`
          : html`${this.label} ${this._renderLogo()}`}
      </rtg-badge>
    `;
  }
}
